/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  ensureCanvasImageApiKey,
  fetchTokenKeysBatch,
  getApiKeys,
} from '@/features/keys/api'
import type { ApiKey } from '@/features/keys/types'
import { useAuthStore } from '@/stores/auth-store'

const CANVAS_BOOTSTRAP_KEY = 'huishitu.canvas.bootstrap'
const CANVAS_SESSION_EVENT_KEY = 'huishitu.canvas.session'
const API_KEYS_PAGE_SIZE = 100

type CanvasLaunchOptions = {
  onUnauthenticated?: () => void
  onMissingApiKey?: () => void
}

const isUsableGroupKey = (group: string) => (apiKey: ApiKey) =>
  String(apiKey.group || '').trim().toLowerCase() === group.trim().toLowerCase() &&
  apiKey.status === 1 &&
  (apiKey.unlimited_quota || apiKey.remain_quota > 0)

const findUsableGroupKey = async (group: string): Promise<ApiKey | undefined> => {
  const firstPage = await getApiKeys({ p: 1, size: API_KEYS_PAGE_SIZE })
  const firstData = firstPage.data
  const firstMatch = firstData?.items.find(isUsableGroupKey(group))
  if (firstMatch) return firstMatch

  const pageSize = firstData?.page_size || API_KEYS_PAGE_SIZE
  const pageCount = Math.ceil((firstData?.total || 0) / pageSize)
  for (let page = 2; page <= pageCount; page += 1) {
    const response = await getApiKeys({ p: page, size: API_KEYS_PAGE_SIZE })
    const match = response.data?.items.find(isUsableGroupKey(group))
    if (match) return match
  }

  return undefined
}

const clearPreviousCanvasCredentials = () => {
  window.sessionStorage.removeItem(CANVAS_BOOTSTRAP_KEY)
  window.localStorage.removeItem('canvas.api-keys-by-provider')
  window.localStorage.removeItem('canvas.api-keys-by-model')
  window.localStorage.removeItem('canvas.text-api-key')
  window.localStorage.setItem(
    CANVAS_SESSION_EVENT_KEY,
    JSON.stringify({ action: 'clear', timestamp: Date.now() })
  )
}

export function useCanvasLaunch(options: CanvasLaunchOptions = {}) {
  const { t } = useTranslation()
  const { auth } = useAuthStore()
  const [isCanvasLaunching, setIsCanvasLaunching] = useState(false)
  const { onUnauthenticated, onMissingApiKey } = options

  const launchCanvas = useCallback(async () => {
    if (!auth.user) {
      if (onUnauthenticated) {
        onUnauthenticated()
      } else {
        toast.error(t('Please sign in before opening Canvas.'))
      }
      return
    }

    // Opening the window synchronously preserves the browser user gesture.
    const canvasWindow = window.open('about:blank', '_blank')
    if (!canvasWindow) {
      toast.error(t('Please allow pop-ups to open Canvas.'))
      return
    }

    clearPreviousCanvasCredentials()
    setIsCanvasLaunching(true)
    try {
      const [defaultGroupKey, textGroupKey, imageGroupKeyResponse] = await Promise.all([
        findUsableGroupKey('default'),
        findUsableGroupKey('gpt'),
        ensureCanvasImageApiKey(),
      ])
      const imageApiKey = imageGroupKeyResponse.data?.key
      if (!defaultGroupKey || !imageGroupKeyResponse.success || !imageApiKey) {
        canvasWindow.close()
        if (onMissingApiKey) {
          onMissingApiKey()
        } else {
          toast.error(t('请在API 密钥创建一个默认的 default分组的令牌然后重试'))
        }
        return
      }

      // The key endpoint is protected by a critical request limiter. Fetching
      // both credentials in one request prevents the optional text key from
      // being dropped when two consecutive key reads are throttled.
      const tokenIds = [defaultGroupKey.id]
      if (textGroupKey) tokenIds.push(textGroupKey.id)
      const keysResponse = await fetchTokenKeysBatch(tokenIds)
      const key = keysResponse.data?.keys[defaultGroupKey.id]
      if (!keysResponse.success || !key) {
        canvasWindow.close()
        toast.error(t('Unable to prepare Canvas. Please try again.'))
        return
      }

      const textApiKey = textGroupKey
        ? keysResponse.data?.keys[textGroupKey.id]
        : undefined

      // sessionStorage is isolated per tab. Write the bootstrap directly into
      // the same-origin about:blank tab before navigating it to Canvas.
      canvasWindow.sessionStorage.setItem(
        CANVAS_BOOTSTRAP_KEY,
        JSON.stringify({
          apiKey: key,
          textApiKey,
          apiOrigin: window.location.origin,
          accountId: auth.user.id,
          imageApiKeys: {
            'gpt-image-2.5-flare': imageApiKey,
            'gpt-image-2.5-sunburst': imageApiKey,
          },
          expiresAt: Date.now() + 60_000,
        })
      )
      canvasWindow.opener = null
      canvasWindow.location.replace(
        new URL('/canvas/', window.location.origin).toString()
      )
    } catch {
      canvasWindow.close()
      toast.error(t('Unable to prepare Canvas. Please try again.'))
    } finally {
      setIsCanvasLaunching(false)
    }
  }, [auth.user, onMissingApiKey, onUnauthenticated, t])

  return { launchCanvas, isCanvasLaunching }
}
